import React, { useEffect, useState } from 'react';
import { MessageCircle, Loader2, X, Send, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * Reusable "Send on WhatsApp" dialog (AiSensy). Works for GST invoices and
 * Non-GST receipts. Loads the enabled AiSensy accounts, lets the user pick
 * which account to send through, confirm the recipient phone, and dispatches
 * the PDF document via the backend.
 *
 * Props: api, docType ('invoice'|'non_gst_receipt'), docId, docLabel,
 *        defaultPhone, onClose
 */
export default function WhatsAppSendDialog({ api, docType, docId, docLabel, defaultPhone, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [phone, setPhone] = useState(defaultPhone || '');
  const [campaign, setCampaign] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api('GET', '/admin/whatsapp/config');
        const list = (r.data?.accounts || []).filter((a) => a.enabled && a.has_key);
        setEnabled(!!r.data?.send_buttons_enabled);
        setAccounts(list);
        const def = list.find((a) => a.is_default) || list[0];
        setAccountId(def?.id || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load WhatsApp accounts');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const selected = accounts.find((a) => a.id === accountId);

  const send = async () => {
    if (!accountId) { toast.error('Pick an AiSensy account'); return; }
    if (!phone?.trim()) { toast.error('Recipient phone is required'); return; }
    setSending(true);
    try {
      const r = await api('POST', '/admin/whatsapp/send-document', {
        account_id: accountId,
        doc_type: docType,
        doc_id: docId,
        phone: phone.trim(),
        campaign_name: campaign.trim() || undefined,
        public_base: window.location.origin,
      });
      toast.success(`Sent on WhatsApp via ${selected?.label || 'account'} → +${r.data?.phone}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'WhatsApp send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="wa-send-dialog">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#25D366] text-white">
          <div className="flex items-center gap-2 font-bold"><MessageCircle size={18} /> Send on WhatsApp</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-500">Deliver <span className="font-semibold text-slate-800">{docLabel}</span> as a PDF to the customer's WhatsApp.</p>

          {loading ? (
            <div className="py-8 text-center text-slate-400"><Loader2 className="inline animate-spin" size={18} /> Loading accounts…</div>
          ) : !enabled ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-3 text-sm flex gap-2" data-testid="wa-send-disabled">
              <AlertTriangle size={15} className="mt-0.5" /> WhatsApp sending is turned off. Enable it in <b>WhatsApp Management → Settings</b>.
            </div>
          ) : accounts.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-3 text-sm flex gap-2" data-testid="wa-send-no-accounts">
              <AlertTriangle size={15} className="mt-0.5" /> No enabled AiSensy accounts. Add one in <b>WhatsApp Management → Settings</b>.
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Send through account</label>
                <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="wa-send-account">
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.label}{a.is_default ? ' · Default' : ''}{a.sender_number ? ` (+91 ${a.sender_number})` : ''}</option>
                  ))}
                </select>
                {selected && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Campaign: <span className="font-mono">{selected.default_campaign_name || '— not set —'}</span>
                    {selected.plan_label ? ` · Plan: ${selected.plan_label}` : ''}
                    {selected.status_label ? ` · ${selected.status_label}` : ''}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipient phone</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" data-testid="wa-send-phone" />
                <p className="text-[11px] text-slate-400 mt-1">Indian numbers auto-prefix +91.</p>
              </div>

              <details>
                <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Override campaign (optional)</summary>
                <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder={selected?.default_campaign_name || 'approved AiSensy campaign name'} className="mt-2" data-testid="wa-send-campaign" />
              </details>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || loading || !enabled || accounts.length === 0}
            className="bg-[#25D366] hover:bg-[#1da851] text-white" data-testid="wa-send-submit">
            {sending ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</> : <><Send size={14} className="mr-1.5" /> Send</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
